import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { ESCAPE_PRIORITY, useEscapeAction } from '@peruselab/structure-editor/components';
import type { IsomerSummary } from '@peruselab/api-client/isomers';
import { Icon } from '../Icon';
import { IsomerThumbnail } from '../IsomerThumbnail';
import type { PaperPlacement } from '../../dnd/payloads';
import type { PaperAdditions } from './paperAdditions';

/** Closes a card popover on Escape or a pointer-down outside `rootRef`. */
export function useDismiss(open: boolean, rootRef: RefObject<HTMLElement | null>, onClose: () => void) {
  // #762 -- Escape through the shared arbiter (overlay priority).
  useEscapeAction(
    ESCAPE_PRIORITY.overlay,
    () => {
      onClose();
      return true;
    },
    open
  );
  useEffect(() => {
    if (!open) return;
    const close = (event: Event) => {
      if (!rootRef.current?.contains(event.target as Node)) onClose();
    };
    globalThis.addEventListener('pointerdown', close);
    return () => globalThis.removeEventListener('pointerdown', close);
  }, [open, rootRef, onClose]);
}

type AddState =
  | { readonly state: 'idle' }
  | { readonly state: 'busy' }
  | { readonly state: 'choosingBox' }
  | {
      readonly state: 'choosing';
      readonly isomers: readonly IsomerSummary[];
      readonly placement: PaperPlacement | undefined;
    }
  | { readonly state: 'error' };

export type AddingToPaper = ReturnType<typeof useAddToPaper>;

/** Adds a card's structure; resolves to the stereoisomers to choose from, or null once added. */
export type AddToPaper = (isomerId?: string, placement?: PaperPlacement) => Promise<readonly IsomerSummary[] | null>;

/** The card's add-to-paper flow, shared by its button and by dragging the card (#699): a molecule
 *  with several stereoisomers asks which one, then adds it where it was placed. */
export function useAddToPaper(addToPaper: AddToPaper | undefined) {
  const [addState, setAddState] = useState<AddState>({ state: 'idle' });
  const add = useCallback(
    async (isomerId?: string, placement?: PaperPlacement) => {
      if (!addToPaper) return;
      setAddState({ state: 'busy' });
      try {
        const isomers = await addToPaper(isomerId, placement);
        setAddState(isomers ? { state: 'choosing', isomers, placement } : { state: 'idle' });
      } catch (error) {
        console.error('Add to paper failed', error);
        setAddState({ state: 'error' });
      }
    },
    [addToPaper]
  );
  const chooseBox = useCallback(() => setAddState({ state: 'choosingBox' }), []);
  const dismiss = useCallback(() => setAddState({ state: 'idle' }), []);
  return { addState, add, chooseBox, dismiss };
}

/**
 * #689 -- "Add to paper": puts this card's structure on the drawing beside what is there, in one
 * undo step. When a molecule has several registered stereoisomers, a picker of their thumbnails
 * (wedges shown) asks which one to add. #700 -- with a reaction arrow on the drawing, it first asks
 * which box. #715 -- My drawings cards have it too.
 */
export function AddToPaperButton({
  moleculeId,
  adding,
  boxes
}: {
  readonly moleculeId: string;
  readonly adding: AddingToPaper;
  readonly boxes: PaperAdditions['boxes'];
}) {
  const { t } = useTranslation();
  const { addState, add, chooseBox, dismiss } = adding;
  const rootRef = useRef<HTMLDivElement | null>(null);
  const open = addState.state === 'choosing' || addState.state === 'choosingBox';
  useDismiss(open, rootRef, dismiss);

  const label = t(addState.state === 'error' ? 'searchResultsPanel.addToPaperFailed' : 'searchResultsPanel.addToPaper');
  return (
    <div className="result-container-button" ref={rootRef}>
      <button
        type="button"
        className="result-container-trigger"
        data-testid="result-add-to-paper"
        aria-label={label}
        title={label}
        aria-expanded={open || (boxes ? false : undefined)}
        disabled={addState.state === 'busy'}
        onClick={() => (open ? dismiss() : boxes ? chooseBox() : void add())}
      >
        <Icon name={addState.state === 'error' ? 'refresh' : 'noteAdd'} size="1.1rem" />
      </button>
      {addState.state === 'choosingBox' && boxes ? (
        <ul className="result-container-menu" aria-label={t('searchResultsPanel.chooseBox')}>
          {boxes.map(({ zone, label: boxLabel }) => (
            <li key={zone}>
              <button type="button" onClick={() => void add(undefined, { zone })}>
                {boxLabel}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {addState.state === 'choosing' ? (
        <ul
          className="result-container-menu result-isomer-menu"
          aria-label={t('searchResultsPanel.chooseStereoisomer')}
        >
          {addState.isomers.map((isomer, index) => (
            <li key={isomer.id}>
              <button
                type="button"
                aria-label={t('searchResultsPanel.addStereoisomer', { number: index + 1 })}
                title={t('searchResultsPanel.addStereoisomer', { number: index + 1 })}
                onClick={() => void add(isomer.id, addState.placement)}
              >
                <IsomerThumbnail moleculeId={moleculeId} isomerId={isomer.id} size={64} />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
